import { formatDateWithOptionsBs, formatTimeWithOptionsBs } from "@/lib/format";
import { sessionFundingParts } from "@/lib/company-funding";
import type { Therapy } from "@/types";

/**
 * Dio seanse koji firma plaća i dio koji klijent zaista uplaćuje, kroz isto
 * pravilo kao server (`sessionFundingParts`): kod sufinansiranja je
 * `clientShare` manji od `priceCharged`, stara seansa `company_credit` bez
 * snapshota je cijela na teret firme, a obična seansa cijela na klijentu. Admin
 * kod "Potvrdi uplatu" poredi izvod banke baš sa `clientPays`.
 */
export function sessionShares(
  t: Pick<Therapy, "price" | "priceCharged" | "companyShare" | "clientShare" | "companyFundingModel" | "paymentMethod">,
): { clientPays: number | null; companyPays: number; cofunded: boolean } {
  const total = t.priceCharged ?? t.price ?? null;
  const parts = sessionFundingParts({
    priceCharged: total,
    paymentMethod: t.paymentMethod ?? null,
    companyFundingModel: t.companyFundingModel ?? null,
    companyShare: t.companyShare ?? null,
    clientShare: t.clientShare ?? null,
  });
  if (!parts.model) return { clientPays: total, companyPays: 0, cofunded: false };
  return { clientPays: parts.clientShare, companyPays: parts.companyShare, cofunded: parts.companyShare > 0 };
}

export function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatDateShort(iso: string): string {
  return formatDateWithOptionsBs(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Sarajevo",
  });
}

export function formatDateLong(date: Date): string {
  return formatDateWithOptionsBs(date, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatTime(iso: string): string {
  return formatTimeWithOptionsBs(iso, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Sarajevo",
  });
}

/** Kad je booking napravljen, npr. "3. nov 2026. u 14:22". */
export function formatBookedAt(iso: string): string {
  return `${formatDateShort(iso)} u ${formatTime(iso)}`;
}

/** Day number + short month for compact date tiles, e.g. { day: "11", month: "jun" }. */
export function dateParts(iso: string): { day: string; month: string } {
  return {
    day: formatDateWithOptionsBs(iso, { day: "numeric", timeZone: "Europe/Sarajevo" }).replace(".", ""),
    month: formatDateWithOptionsBs(iso, { month: "short", timeZone: "Europe/Sarajevo" }).replace(".", ""),
  };
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
